<template>
  <div>
    <vx-card
      class="mb-5"
      title="Here is some information you will need in order to complete the Activities."
    >
      <h4 class="mb-5">A Day in the Life of a Veterinarian</h4>
      <p class="mb-5">A brief insight into the world of the veterinarian.</p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <responsive-iframe-youtube
            :src="intro_videos[1].video"
            :posterimg="intro_videos[1].image"
          />
        </div>
      </div>

      <h4 class="mb-5">The Role of a Registered Veterinary Nurse</h4>
      <p class="mb-5">A brief insight into the world of the veterinary nurse.</p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <responsive-iframe-youtube
            :src="intro_videos[2].video"
            :posterimg="intro_videos[2].image"
          />
        </div>
      </div>

      <h4 class="mb-5">Outside a Regular Pet Clinic</h4>
      <p class="mb-5">For a look at how the more unusual animals are cared for, take a look at:</p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <responsive-iframe-youtube
            :src="intro_videos[3].video"
            :posterimg="intro_videos[3].image"
          />
        </div>
      </div>
    </vx-card>
    <vx-card>
      <h4 class="mb-5">How to Scrub Up - and Why!</h4>
      <p class="mb-5">
        "Don't forget to wash your hands!" is the plea from mums everywhere...but there's washing,
        then there's the highly specialised scrubbing that surgeons carry out before each and every
        operation.
      </p>
      <p class="mb-5">
        ARH Specialist veterinary surgeon Dr Sarah Goldsmid has lost count of the number of times
        she's scrubbed up for an operation but says there's no way she'd ever consider taking a
        shortcut or rushing this crucial step.
      </p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <responsive-iframe-youtube
            :src="intro_videos[4].video"
            :posterimg="intro_videos[4].image"
          />
        </div>
      </div>

      <h4 class="mb-5">Veterinary Basics: Things You Should Know Before Going Into a Clinic</h4>
      <p class="mb-5">By Jess Cliffe – A VET student in New Zealand</p>
      <p class="mb-5">
        This is a brief introduction to basic veterinary information for spending time in a small
        animal clinic: including temperatures, heart rates, respiratory rates, blood pressure,
        positioning, anatomy and radiographs!
      </p>

      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-1/2 w-full mb-5">
          <p class="mb-5">Timestamps:</p>
          <p class="mb-5"><u>00:01:15</u> - TPRs</p>
          <p class="mb-5"><u>00:03:14</u> - Blood pressure</p>
          <p class="mb-5"><u>00:04:08</u> - Positioning</p>
          <p class="mb-5"><u>00:05:49</u> - Anatomy</p>
          <p class="mb-5"><u>00:08:17</u> - Mucus membranes</p>
          <p class="mb-5"><u>00:09:36</u> – Radiographs</p>
        </div>
        <div class="vx-col xl:w-1/2 lg:w-1/2 w-full mb-5">
          <video-trigger
            :imageSrc="intro_videos[5].image"
            @click="showIframePopup(intro_videos[5].video)"
          ></video-trigger>
        </div>
      </div>

      <h4 class="mb-5">Veterinary Neurological Exam</h4>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <responsive-iframe-youtube
            :src="intro_videos[6].video"
            :posterimg="intro_videos[6].image"
          />
        </div>
      </div>
    </vx-card>
    <vx-card>
      <h4 class="mb-5">Animal Blood Types – Did you know?</h4>

      <h4 class="mb-5">Apes</h4>
      <p class="mb-5">
        Although the great apes share our blood groups (A, B, AB, O), blood types vary between
        species.
      </p>
      <ul class="default-list">
        <li>
          Bonobos and chimpanzees have predominantly type A blood, while orangutans have all four
          types
        </li>
        <li>
          Gorilla blood is a bit of a mystery. Samples always come back as type O, but research and
          anecdotal evidence suggest that individuals don’t, in fact, have the same blood type.
        </li>
      </ul>

      <h4 class="mb-5">Canines</h4>
      <p class="mb-5">
        Dogs have 8 blood types referred to as DEA (dog erythrocyte antigen) 1–8, with over 13 blood
        groups described.
      </p>
      <ul class="default-list">
        <li>
          Dog blood groups are unique in that not all groups have naturally occurring antibodies
        </li>
        <li>
          Dog blood groups don’t immediately cross-react with incompatible blood types. This means
          that an immediate match does not need to made, and an initial unmatched transfusion can be
          given.
        </li>
      </ul>

      <h4 class="mb-5">Felines</h4>
      <p class="mb-5">Felines only have three blood types: A, B, and AB.</p>
      <ul class="default-list">
        <li>
          Blood group is largely breeding specific, with Siamese and related breeds, Burmese,
          Tonkinese, and Russian Blue breed being exclusively type A
        </li>
        <li>
          Transfusion of A blood into B type can cause a severe clinical reaction, while giving
          B-type blood to an A-type cat results in a much milder cross reaction
        </li>
        <li>The same trend of dominant A blood groups is seen in wild cats</li>
        <li>
          Feline blood types are the result of two alleles on the same gene: one conferring type A
          blood and one conferring type B. It’s hypothesised that the type A allele is dominant over
          type B and this is why the type A blood type dominates.
        </li>
      </ul>

      <h4 class="mb-5">Cephalopods, crustaceans, and fish</h4>
      <p class="mb-5">
        Not much is reported on the blood groups of sea-faring animals, but their blood has some
        interesting features.
      </p>
      <ul class="default-list">
        <li>
          Some octopus and crustaceans use the copper-rich protein hemocyanin rather than iron-rich
          haemoglobin to transport oxygen around the body
        </li>
        <li>The high copper level results in blue blood</li>
        <li>
          Some arctic fish have colourless blood. They require no oxygen carrier and can extract
          dissolved oxygen from their surroundings.
        </li>
      </ul>
      <p class="mb-5">
        Sourced from <a href="https://www.vetvoice.com.au" target="_blank">www.vetvoice.com.au</a>
      </p>
    </vx-card>
    <vx-card>
      <h4 class="mb-5">Educating Pet Owners</h4>
      <p class="mb-5">
        Understanding what is required to provide good care to a range of animal species is an
        essential component of being a veterinarian.
      </p>
      <p class="mb-5">
        It is pertinent that we promote good animal care and discuss the consequences when animals
        are not cared for appropriately with our clients.
      </p>
      <p class="mb-5">
        Thankfully veterinarians have access to many resources to assist them in educating pet
        owners so that pets can be more easily assessed and cared for.
      </p>
      <p class="mb-5">
        Below is an example of how a VET can gather quality information from a pet owner to
        establish the existence or severity of a condition.
      </p>

      <h4 class="mb-5 text-center">Does Your Dog Suffer Noise Aversion?</h4>

      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <img
            src="@/assets/images/experiences/vet/lr1.png"
            alt="Checklist: Does your dog suffer from noise aversion? Use it to identify triggers and behaviours. Noise triggers: fireworks, thunder, celebrations, construction work, traffic or street noise, other. Behaviours during noise events: pacing, lip licking, trembling or shaking, panting, excessive vigilance, cowering, hiding, brow furrowed or ears back, freezing or immobility, owner-seeking behaviour or clinginess, refusing to eat, yawning, vocalisation. How often the dog reacts: once or twice a year, once or twice a month, weekly, daily. Intensity: mild, moderate or severe impact on quality of life. Recovery time: immediately, within an hour, several hours, or a day or more after the trigger stops. Copyright 2016 Zoetis Services LLC."
            class="mx-auto img-responsive"
          />
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import VideoTrigger from '@/components/UI/VideoTrigger';

import store from '@/store/store';

export default {
  components: { VideoTrigger },
  data() {
    return {
      intro_videos: {
        1: {
          image: require('@/assets/images/experiences/vet/vet_day_in_life_splash.jpg'),
          video: 'https://www.youtube.com/embed/ELcONPKag74',
        },
        2: {
          image: require('@/assets/images/experiences/vet/vet_nurse_day_life_splash.jpg'),
          video: 'https://www.youtube.com/embed/l-E59LT7JQw',
        },
        3: {
          image: require('@/assets/images/experiences/vet/outside_pet_clinic_splash.jpg'),
          video: 'https://www.youtube.com/embed/pzFVU-kp1eo',
        },
        4: {
          image: require('@/assets/images/experiences/vet/scrubbing_splash.jpg'),
          video: 'https://www.youtube.com/embed/BfVlflt_1xE',
        },
        5: {
          image: require('@/assets/images/experiences/vet/vet_basics_splash.jpg'),
          video: 'https://www.youtube.com/embed/lIY2sYTUh2Y?autoplay=1',
        },
        6: {
          image: require('@/assets/images/experiences/vet/neurological_exam_splash.jpg'),
          video: 'https://www.youtube.com/embed/aTlFls2GISw',
        },
      },
      showVideoPopupYt: false,
      ytVideoUrl: false,
    };
  },
  provide: function () {
    return {
      closePopup: this.closePopup,
    };
  },
  methods: {
    showIframePopup(video) {
      store.dispatch('popup/showIframePopup', video);
    },
    showVideoYt(url) {
      this.ytVideoUrl = url;
      this.showVideoPopupYt = true;
    },
  },
  computed: {
    activeExperienceID() {
      return store.getters['authStudent/activeExperienceID'];
    },
    experience() {
      const loginType = localStorage.getItem('login_type');
      if (loginType == 'student') {
        const experiences = store.getters['authStudent/experiences'];
        return experiences.find((i) => i.id == this.activeExperienceID);
      } else {
        return store.getters['authTeacher/experience'];
      }
    },
  },
  beforeMount() {
    store.commit('authStudent/setActiveExperienceID', this.$route.params.id);
  },
};
</script>
